/**
 * Bulk image analysis v2 ("Smart photos") — UI types.
 *
 * Mirrors json-lib-api `com.nine.api.bulkimageanalysis.model` (55 classes) and
 * product/docs/bulk-image-analysis-v2-design.md (API §9, states §4). The wire is
 * camelCase JSON; every enum has an UNKNOWN fallback so a value from a newer
 * backend can never crash a screen (the converters map anything unexpected to
 * "UNKNOWN" and the UI treats it as "not actionable").
 *
 * Differences from the wire, all made in
 * services/converters/backendToUi/bulkImageAnalysisV2Convertor.ts:
 *  - `Instant` fields are ISO strings (or null);
 *  - absent lists are [] and absent text is null;
 *  - BiaImageNote's `product` field travels as `isProduct` and is `isProduct` here;
 *  - a payload item's `product` is the INNER Product map. On the wire it is a
 *    CreateProductRq (`{ product: {...} }`); the converter unwraps it so the
 *    review screen sees the same `{ clientItemId, product: {...} }` item shape
 *    the existing bulk flow already handles.
 *
 * Plan: docs/plans/bulk-image-analysis-v2-ui-plan.md.
 */

// ── Enums ────────────────────────────────────────────────────────────────────

export const BIA_SESSION_STATUSES = [
  "AWAITING_UPLOAD",
  "UPLOADED",
  "PLANNING",
  "PLAN_FAILED",
  "PLAN_READY",
  "REVISING",
  "PLAN_CONFIRMED",
  "GENERATING",
  "READY_FOR_REVIEW",
  "CREATING",
  "COMPLETED",
  "CANCELLED",
  "EXPIRED",
  "UNKNOWN",
] as const;
export type BiaSessionStatus = (typeof BIA_SESSION_STATUSES)[number];

export const BIA_IMAGE_STATUSES = ["PENDING_UPLOAD", "UPLOADED", "EXCLUDED", "DELETED", "FAILED", "UNKNOWN"] as const;
export type BiaImageStatus = (typeof BIA_IMAGE_STATUSES)[number];

export const BIA_EXCLUSION_REASONS = [
  "DUPLICATE_EXACT",
  "DUPLICATE_NEAR",
  "SCREENSHOT",
  "BLURRY",
  "WATERMARK",
  "OTHER_BRAND",
  "COLLAGE",
  "NOT_PRODUCT",
  "MODERATION_BLOCKED",
  "CURATOR",
  "UNKNOWN",
] as const;
export type BiaExclusionReason = (typeof BIA_EXCLUSION_REASONS)[number];

export const BIA_EXCLUDED_BY = ["SYSTEM", "MODEL", "CURATOR", "UNKNOWN"] as const;
export type BiaExcludedBy = (typeof BIA_EXCLUDED_BY)[number];

export const BIA_IMAGE_FLAGS = ["LOW_RESOLUTION", "NEAR_DUPLICATE", "NOTE_FAILED", "UNKNOWN"] as const;
export type BiaImageFlag = (typeof BIA_IMAGE_FLAGS)[number];

export const BIA_IMAGE_QUALITIES = [
  "OK",
  "BLURRY",
  "SCREENSHOT",
  "WATERMARK",
  "COLLAGE",
  "OTHER_BRAND",
  "NOT_PRODUCT",
  "UNKNOWN",
] as const;
export type BiaImageQuality = (typeof BIA_IMAGE_QUALITIES)[number];

export const BIA_JOB_TYPES = ["PLAN", "REVISE", "GENERATE", "UNKNOWN"] as const;
export type BiaJobType = (typeof BIA_JOB_TYPES)[number];

export const BIA_JOB_STATUSES = ["QUEUED", "RUNNING", "SUCCEEDED", "FAILED", "CANCELLED", "UNKNOWN"] as const;
export type BiaJobStatus = (typeof BIA_JOB_STATUSES)[number];

export const BIA_PLAN_SOURCES = ["AI_PLAN", "AI_REVISE", "MANUAL", "RESTORE", "UNKNOWN"] as const;
export type BiaPlanSource = (typeof BIA_PLAN_SOURCES)[number];

export const BIA_PRODUCT_STATUSES = [
  "PLANNED",
  "GENERATING",
  "READY",
  "FAILED",
  "CREATING",
  "CREATED",
  "CREATE_FAILED",
  "SKIPPED",
  "UNKNOWN",
] as const;
export type BiaProductStatus = (typeof BIA_PRODUCT_STATUSES)[number];

export const BIA_PRODUCT_FLAGS = [
  "LOW_CONFIDENCE",
  "VARIANT_SPLIT",
  "FIELDS_REFUSED",
  "SINGLE_PHOTO_FALLBACK",
  "POSSIBLE_DUPLICATE",
  "UNKNOWN",
] as const;
export type BiaProductFlag = (typeof BIA_PRODUCT_FLAGS)[number];

export const BIA_PROGRESS_STEPS = [
  "BRIEF",
  "NOTES",
  "LOOKING",
  "TREE",
  "REVISE",
  "CHECKING",
  "NAMING",
  "GENERATE",
  "TITLES",
  "CREATE",
] as const;
export type BiaProgressStep = (typeof BIA_PROGRESS_STEPS)[number];

export const BIA_CREATE_ITEM_STATUSES = ["CREATED", "ALREADY_CREATED", "FAILED", "SKIPPED", "UNKNOWN"] as const;
export type BiaCreateItemStatus = (typeof BIA_CREATE_ITEM_STATUSES)[number];

export const BIA_GENERATE_MODES = ["REALTIME", "BATCH", "UNKNOWN"] as const;
export type BiaGenerateMode = (typeof BIA_GENERATE_MODES)[number];

/** `GET /sessions?status=`. ACTIVE = any non-terminal status. */
export type BiaSessionFilter = "ACTIVE" | "ENDED" | "ALL";

// ── Config and small records ─────────────────────────────────────────────────

export interface BiaConfig {
  enabled: boolean;
  maxImages: number;
  maxFileBytes: number;
  acceptedContentTypes: string[];
  clientMaxEdgePx: number;
  uploadBatchSize: number;
  revisionLimit: number;
  maxInstructionChars: number;
  createMaxPerCall: number;
  generateMode: BiaGenerateMode;
  /** Photos the caller may still analyse today; null when the server sends none. */
  remainingImagesToday: number | null;
}

/** A stored failure (session, job or image). `message` is curator-safe by contract. */
export interface BiaError {
  code: string;
  message: string;
  retryable: boolean;
  at: string | null;
}

/** A per-item failure: upload confirm, upload url rejection, create. */
export interface BiaItemError {
  /** What it is about: an imageId, a clientRef or a clientItemId, depending on the call. */
  ref: string;
  code: string;
  message: string;
  retryable: boolean;
}

export interface BiaProgress {
  label: string;
  done: number;
  total: number;
}

export interface BiaJobInfo {
  jobId: string;
  type: BiaJobType;
  status: BiaJobStatus;
  attempts: number;
  startedAt: string | null;
  finishedAt: string | null;
  error: BiaError | null;
}

// ── Session ──────────────────────────────────────────────────────────────────

export interface BiaSession {
  sessionId: string;
  businessId: string;
  businessName: string | null;
  setupId: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdByAdmin: boolean;
  status: BiaSessionStatus;
  customerInstructions: string | null;
  curatorNotes: string | null;
  currentPlanVersion: number | null;
  confirmedPlanVersion: number | null;
  planStale: boolean;
  revisionsUsed: number;
  revisionLimit: number;
  imageCount: number;
  excludedCount: number;
  pendingUploadCount: number;
  productCount: number;
  productsReady: number;
  productsFailed: number;
  productsCreated: number;
  progress: BiaProgress | null;
  activeJob: BiaJobInfo | null;
  lastJob: BiaJobInfo | null;
  error: BiaError | null;
  createdAt: string | null;
  updatedAt: string | null;
  expiresAt: string | null;
}

export interface BiaSessionListItem {
  sessionId: string;
  businessId: string;
  businessName: string | null;
  status: BiaSessionStatus;
  imageCount: number;
  productCount: number;
  productsCreated: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateBiaSessionForm {
  businessId: string;
  setupId?: string | null;
  customerInstructions?: string;
  curatorNotes?: string;
}

export interface UpdateBiaSessionForm {
  customerInstructions?: string;
  curatorNotes?: string;
}

// ── Images ───────────────────────────────────────────────────────────────────

export interface BiaImageNote {
  /** Wire name `isProduct`. */
  isProduct: boolean;
  kind: string | null;
  category: string | null;
  attributes: Record<string, string>;
  visibleText: string | null;
  quality: BiaImageQuality;
  qualityNote: string | null;
  distinguishing: string | null;
}

export interface BiaImage {
  imageId: string;
  /** 1-based position the plan and the customer questions refer to ("photo 24"). */
  idx: number;
  fileName: string | null;
  thumbnailUrl: string | null;
  imageUrl: string | null;
  widthPx: number | null;
  heightPx: number | null;
  status: BiaImageStatus;
  exclusionReason: BiaExclusionReason | null;
  excludedBy: BiaExcludedBy | null;
  duplicateOf: string | null;
  nearDuplicateOf: string | null;
  flags: BiaImageFlag[];
  note: BiaImageNote | null;
  error: BiaError | null;
}

export interface BiaImagePage {
  items: BiaImage[];
  nextCursor: string | null;
}

export interface UpdateBiaImageForm {
  excluded: boolean;
  reason?: BiaExclusionReason;
}

// ── Upload ───────────────────────────────────────────────────────────────────

export interface BiaUploadFileRq {
  clientRef: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

export interface BiaUploadUrl {
  clientRef: string;
  imageId: string;
  uploadUrl: string;
  expiresAt: string | null;
}

export interface BiaUploadUrlsRs {
  uploads: BiaUploadUrl[];
  rejected: BiaItemError[];
}

export interface BiaConfirmUploadsRs {
  confirmed: BiaImage[];
  failed: BiaItemError[];
}

// ── Plan ─────────────────────────────────────────────────────────────────────

export interface BiaTreeNode {
  /** Plan-local key (a node is addressed by it inside groups). */
  key: string;
  name: string;
  /** An existing business section this node maps to, when the model matched one. */
  existingSectionId: string | null;
  /** The real section id, after the plan was confirmed. */
  sectionId: string | null;
  /** True when confirming created this section (so unconfirm may delete it). */
  createdByConfirm: boolean;
  productCount: number;
  children: BiaTreeNode[];
}

export interface BiaGroup {
  key: string;
  imageIds: string[];
  /** Key of the tree node the product is filed under. */
  leafKey: string | null;
  titleHint: string | null;
  /** Key of the group this is a variant of. */
  variantOfKey: string | null;
  confidence: number | null;
  reason: string | null;
}

export interface BiaExclusion {
  imageId: string;
  reason: BiaExclusionReason;
  by: BiaExcludedBy;
  note: string | null;
}

export interface BiaPlanWarning {
  code: string;
  message: string;
  /** Photo numbers / keys the warning is about. */
  refs: string[];
}

export interface BiaPlan {
  version: number;
  source: BiaPlanSource;
  basedOnVersion: number | null;
  instruction: string | null;
  tree: BiaTreeNode[];
  groups: BiaGroup[];
  excluded: BiaExclusion[];
  questions: string[];
  warnings: BiaPlanWarning[];
  summary: string | null;
  confirmed: boolean;
  createdAt: string | null;
  createdByName: string | null;
}

/** What `PUT /plan` carries: the editable part of a plan. */
export interface BiaPlanBody {
  tree: BiaTreeNode[];
  groups: BiaGroup[];
  excluded: BiaExclusion[];
}

export interface BiaPlanVersionInfo {
  version: number;
  source: BiaPlanSource;
  instruction: string | null;
  productCount: number;
  sectionCount: number;
  createdAt: string | null;
  createdByName: string | null;
  current: boolean;
  confirmed: boolean;
}

export type BiaPlanExportFormat = "TEXT" | "JSON";

export interface BiaPlanExport {
  format: BiaPlanExportFormat;
  text: string | null;
  plan: BiaPlan | null;
  businessName: string | null;
  generatedAt: string | null;
}

// ── Payload and create ───────────────────────────────────────────────────────

export interface BiaPayloadItem {
  /** The server's product id for this group; the idempotency key of create. */
  clientItemId: string;
  status: BiaProductStatus;
  flags: BiaProductFlag[];
  confidence: number | null;
  /** Section names from the tree root down to the product's node. */
  sectionPath: string[];
  /** The inner Product (unwrapped from the wire's CreateProductRq); null when generation failed. */
  product: Record<string, unknown> | null;
  error: BiaError | null;
}

export interface BiaPayloadPage {
  items: BiaPayloadItem[];
  nextCursor: string | null;
  total: number;
}

/** An item of the existing bulk review: what lands in the payload text. */
export interface BiaReviewItem {
  clientItemId: string;
  product: Record<string, unknown>;
}

export interface BiaCreateItemRq {
  clientItemId: string;
  /** The inner Product; the converter wraps it as CreateProductRq. */
  product: Record<string, unknown>;
}

export interface BiaCreateItemResult {
  clientItemId: string;
  status: BiaCreateItemStatus;
  productId: string | null;
  error: BiaItemError | null;
}

export interface BiaCreateProductsRs {
  results: BiaCreateItemResult[];
  createdCount: number;
  failedCount: number;
  sessionStatus: BiaSessionStatus | null;
}

// ── Client-side rejection ────────────────────────────────────────────────────

/** What every thunk rejects with (same idea as CatalogImportRejection). */
export interface BiaRejection {
  /** Curator-facing text. */
  message: string;
  /** "16319", "HTTP_503", "CLIENT_NETWORK", ... */
  code: string;
  httpStatus: number;
  retryable: boolean;
  /** `additionalDetails` of the error envelope, when present. */
  details: Record<string, unknown> | null;
}

// ── Defaults and constants ───────────────────────────────────────────────────

export const BIA_DEFAULT_MAX_IMAGES = 300;
export const BIA_DEFAULT_MAX_FILE_BYTES = 8 * 1024 * 1024;
export const BIA_DEFAULT_CLIENT_MAX_EDGE = 1600;
export const BIA_DEFAULT_UPLOAD_BATCH = 50;
export const BIA_DEFAULT_CREATE_CHUNK = 25;
export const BIA_DEFAULT_MAX_INSTRUCTION_CHARS = 2000;
/** Accepted when the server's config lists none. */
export const BIA_DEFAULT_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
/** What the file picker offers; HEIC is tried and rejected per file when the browser cannot decode it. */
export const BIA_PICKER_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif";
/** Maximum levels of the section tree (section / subsection / sub-subsection). */
export const BIA_MAX_TREE_DEPTH = 3;

// ── Status helpers ───────────────────────────────────────────────────────────

/** A job is running on the server: poll. */
export function isBiaBusy(status: BiaSessionStatus): boolean {
  return status === "PLANNING" || status === "REVISING" || status === "GENERATING";
}

export function isBiaTerminal(status: BiaSessionStatus): boolean {
  return status === "COMPLETED" || status === "CANCELLED" || status === "EXPIRED";
}

/** Photos may be added or removed. */
export function biaCanChangePhotos(status: BiaSessionStatus): boolean {
  return status === "AWAITING_UPLOAD" || status === "UPLOADED" || status === "PLAN_READY" || status === "PLAN_FAILED";
}

/** Customer instructions / curator notes may be edited (until the plan is confirmed, not while a job runs). */
export function biaCanEditInstructions(status: BiaSessionStatus): boolean {
  return biaCanChangePhotos(status);
}

export type BiaStep = "upload" | "plan" | "products" | "review";

export const BIA_STEPS: { id: BiaStep; label: string }[] = [
  { id: "upload", label: "Upload" },
  { id: "plan", label: "Plan" },
  { id: "products", label: "Products" },
  { id: "review", label: "Review" },
];

/** Status -> step (plan §4). Terminal statuses have no step (a card is shown instead). */
export function biaStepOf(status: BiaSessionStatus): BiaStep | null {
  switch (status) {
    case "AWAITING_UPLOAD":
    case "UPLOADED":
      return "upload";
    case "PLANNING":
    case "PLAN_FAILED":
    case "PLAN_READY":
    case "REVISING":
      return "plan";
    case "PLAN_CONFIRMED":
    case "GENERATING":
      return "products";
    case "READY_FOR_REVIEW":
    case "CREATING":
      return "review";
    default:
      return null;
  }
}

/** Next unfinished step after `step` (for the stepper's "done" marks). */
export function biaStepIndex(step: BiaStep | null): number {
  return step === null ? -1 : BIA_STEPS.findIndex((s) => s.id === step);
}
